import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Params } from '@angular/router';

import { combineLatest, Observable, map } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { JsConfig } from '@fcom/core/interfaces';
import { CmsPageComponentMetaData } from '@fcom/common';
import { BreadcrumbItem } from '@fcom/common/interfaces';
import { ReplaceParams, unique } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { BreadcrumbService } from '@fcom/common/services';
import { RootPaths } from '@fcom/core/constants';

import {
  FlightPair,
  OndLocationData,
  OndLocationsResponse,
  OndMarketingRoutesPath,
  OndPairEnhanced,
  OndPairEnhancedResponse,
} from '../interfaces';
import { OndMarketingOffersService } from './ond-marketing-offers.service';
import { getFlightType, getLocationName, isCountryCode, isDestinationCode, transformLocationCode } from './utils';

@Injectable()
export class OndMarketingSeoService {
  private readonly offersService = inject(OndMarketingOffersService);
  private readonly languageService = inject(LanguageService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly breadcrumbTranslations = this.languageService.translateMultipleSignal([
    'ondMarketing.breadcrumb.flights',
    'ondMarketing.breadcrumb.fromCity',
    'ondMarketing.breadcrumb.cityToCity',
    'ondMarketing.breadcrumb.fromCountry',
    'ondMarketing.breadcrumb.countryToCountry',
  ]);

  readonly breadcrumbs = computed(() => {
    const i18n = this.breadcrumbTranslations();

    const baseUrl = `/${this.languageService.langValue}/${RootPaths.OND_MARKETING_LANDING_ROOT}`;
    const baseCrumb = {
      title: i18n?.[0],
      url: baseUrl,
    };

    return {
      [RootPaths.OND_MARKETING_LANDING_ROOT]: [baseCrumb],
      [OndMarketingRoutesPath.FROM]: [
        baseCrumb,
        {
          title: i18n?.[1],
          url: `${baseUrl}/${OndMarketingRoutesPath.FROM}`,
        },
      ],
      [OndMarketingRoutesPath.CITY_TO_CITY]: [
        baseCrumb,
        {
          title: i18n?.[2],
          url: `${baseUrl}/${OndMarketingRoutesPath.CITY_TO_CITY}`,
        },
      ],
      [OndMarketingRoutesPath.FROM_COUNTRY]: [
        baseCrumb,
        {
          title: i18n?.[3],
          url: `${baseUrl}/${OndMarketingRoutesPath.FROM_COUNTRY}`,
        },
      ],
      [OndMarketingRoutesPath.COUNTRY_TO_COUNTRY]: [
        baseCrumb,
        {
          title: i18n?.[4],
          url: `${baseUrl}/${OndMarketingRoutesPath.COUNTRY_TO_COUNTRY}`,
        },
      ],
    };
  });

  /**
   * This is necessary in order to wait for translations to be ready, otherwise its possible
   * (via race condition) to set the breadcrumb before they are loaded.
   */
  private readonly pendingBreadcrumb = signal<
    { path: OndMarketingRoutesPath | null; lastBreadCrumb?: BreadcrumbItem } | undefined
  >(undefined);

  constructor() {
    effect(() => {
      const pending = this.pendingBreadcrumb();
      if (pending === undefined) {
        return;
      }
      const breadcrumbs = this.breadcrumbs();
      if (!this.breadcrumbTranslations()?.[0]) {
        return;
      }
      untracked(() => {
        let items: BreadcrumbItem[];

        if (!pending.path) {
          items = breadcrumbs[RootPaths.OND_MARKETING_LANDING_ROOT];
        } else {
          items = [...breadcrumbs[pending.path], ...(pending.lastBreadCrumb ? [pending.lastBreadCrumb] : [])];
        }

        this.breadcrumbService.setBreadcrumb(items);
      });
    });
  }

  getFlightParams(params: Params, langKeyValue: string): Observable<FlightPair> {
    return combineLatest([
      this.offersService.getAirportName(transformLocationCode(params.originLocationCode), langKeyValue),
      this.offersService.getAirportName(transformLocationCode(params.destinationLocationCode), langKeyValue),
    ]).pipe(
      map(([originAirport, destinationAirport]) => ({
        origin: transformLocationCode(params.originLocationCode) || '',
        originAirport,
        destinationAirport,
        destination: transformLocationCode(params.destinationLocationCode) || '',
        ignoredSEOTitle: params.ignoredSEOTitle || '',
      })),
      finShare()
    );
  }

  getLocations(flightPair: FlightPair): Observable<OndLocationsResponse> {
    const vitalLocations: string[] = flightPair.destination
      ? [flightPair.origin, flightPair.destination]
      : [flightPair.origin];
    return this.offersService.getLocationData(vitalLocations);
  }

  getLinks(flightPair: FlightPair): Observable<OndPairEnhancedResponse> {
    let originLocationCode: string | undefined;
    let destinationLocationCode: string | undefined;
    let originCountry: string | undefined;
    let destinationCountry: string | undefined;

    if (!isCountryCode(flightPair.origin)) {
      originLocationCode = flightPair.origin;
      destinationLocationCode = flightPair.destination ? flightPair.destination : undefined;
    } else {
      originCountry = flightPair.origin;
      destinationCountry = flightPair.destination ? flightPair.destination : undefined;
    }

    return this.offersService.fetchLocationPairs({
      origin: originLocationCode,
      destination: destinationLocationCode,
      originCountry,
      destinationCountry,
    });
  }

  getPageMeta(flightPair: FlightPair, locations: OndLocationsResponse): Observable<string[]> {
    const translationData: ReplaceParams = {
      origin: getLocationName(flightPair.origin, locations),
      originLocationCode: flightPair.origin,
      destination: getLocationName(flightPair.destination, locations),
      destinationLocationCode: flightPair.destination,
    };
    const flightType = getFlightType(flightPair);
    return combineLatest([
      this.languageService.translate(`ondMarketing.metaData.${flightType}.title`, translationData),
      this.languageService.translate(`ondMarketing.metaData.${flightType}.description`, translationData),
      this.languageService.translate(`ondMarketing.seoUrl.${getFlightType(flightPair, false)}`, translationData),
    ]);
  }

  applyCityToCityMetaOverride(
    pairs: OndPairEnhanced[],
    lang: string,
    base: { title: string; description: string }
  ): { title: string; description: string; detailText?: string } {
    let { title, description } = base;
    let detailText: string | undefined;

    pairs
      .filter((pair) => lang === pair.lang)
      .forEach((pair) => {
        if (pair.title) {
          title = pair.title;
        }
        if (pair.htmlDescription) {
          description = pair.htmlDescription;
        }
        if (pair.detailText) {
          detailText = pair.detailText;
        }
      });

    return { title, description, ...(detailText !== undefined ? { detailText } : {}) };
  }

  buildHreflangLinks(
    pairs: OndPairEnhanced[],
    path: string,
    cfg: Pick<JsConfig, 'allowedLocales' | 'baseUrl'>
  ): CmsPageComponentMetaData[] {
    const pathArray = path.split('/');
    const newPath = pathArray.splice(2);

    return pairs
      .map((pair) => pair.lang)
      .filter(unique)
      .map((language) => {
        const formattedLang = cfg.allowedLocales[language]?.replace('_', '-');
        return {
          attributes: {
            rel: 'alternate',
            hreflang: formattedLang ?? language,
            href: `${cfg.baseUrl}/${language}/${newPath.join('/')}`,
          },
          tagName: 'link',
        };
      });
  }

  buildBreadcrumb(
    flightParams: FlightPair,
    locations: OndLocationsResponse,
    dst: BreadcrumbItem,
    currentPath: string,
    flightsToLabel: string
  ): BreadcrumbItem[] {
    if (isDestinationCode(flightParams.origin) && isDestinationCode(flightParams.destination)) {
      const destinationLocationData = locations[flightParams.destination] as OndLocationData;
      const originLocationData = locations[flightParams.origin] as OndLocationData;

      const countryCrumb: BreadcrumbItem = {
        title: destinationLocationData.country,
        url: destinationLocationData.countryUrl,
      };
      const destinationCrumb: BreadcrumbItem = {
        title: `${flightsToLabel} ${destinationLocationData.cityName}`,
        url: destinationLocationData.destinationUrl,
      };
      const currentCrumb: BreadcrumbItem = {
        title: `${originLocationData.cityName} ${destinationLocationData.cityName}`,
        url: currentPath,
      };

      return [dst, countryCrumb, destinationCrumb, currentCrumb];
    }

    return [dst];
  }

  setBreadcrumb(path: OndMarketingRoutesPath, lastBreadCrumb?: BreadcrumbItem): void {
    this.pendingBreadcrumb.set({ path: path ?? null, lastBreadCrumb });
  }
}
